import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Clock, Trash2, Upload, Database, Save, ChevronRight, User as UserIcon, MessageSquare, X } from 'lucide-react';
import api from '../../utils/api';

/**
 * SankatVIPPanel — admin control for the paid VIP (200 AT / 15-min default) sessions.
 *
 * Three panes:
 *  1. Duration slider (60s .. 3600s) with saved config
 *  2. All saved paid sessions across users — view full chat, hard-delete
 *  3. Per-avatar Knowledge Base — upload TXT/PDF files, list & delete sources
 */
const SankatVIPPanel = () => {
  const [config, setConfig] = useState({ duration_sec: 900, auto_delete_hours: 48 });
  const [durationInput, setDurationInput] = useState(900);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [avatars, setAvatars] = useState([]);
  const [kbAvatarId, setKbAvatarId] = useState('');
  const [kbSources, setKbSources] = useState([]);
  const [kbFile, setKbFile] = useState(null);
  const [kbLabel, setKbLabel] = useState('');
  const [uploading, setUploading] = useState(false);

  const loadConfig = () => api.get('/sankat-mochan/admin/paid-config').then((r) => {
    setConfig(r.data);
    setDurationInput(r.data.duration_sec || 900);
  }).catch(() => {});
  const loadSessions = () => api.get('/sankat-mochan/admin/paid-sessions').then((r) => setSessions(r.data.sessions || [])).catch(() => {});
  const loadAvatars = () => api.get('/sankat-mochan/avatars').then((r) => setAvatars(r.data.avatars || [])).catch(() => {});
  const loadKB = (aid) => {
    if (!aid) return setKbSources([]);
    api.get(`/sankat-mochan/admin/avatar/${aid}/knowledge`).then((r) => setKbSources(r.data.sources || [])).catch(() => setKbSources([]));
  };

  useEffect(() => { loadConfig(); loadSessions(); loadAvatars(); }, []);
  useEffect(() => { if (kbAvatarId) loadKB(kbAvatarId); }, [kbAvatarId]);

  const saveDuration = async () => {
    try {
      await api.post('/sankat-mochan/admin/paid-config', { duration_sec: durationInput });
      toast.success(`VIP duration saved: ${Math.floor(durationInput / 60)} min`);
      loadConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const deleteSession = async (id) => {
    if (!window.confirm('Delete this saved session (hard delete)?')) return;
    try {
      await api.delete(`/sankat-mochan/admin/paid-sessions/${id}`);
      setSessions((prev) => prev.filter((s) => s.id !== id));
      if (selectedSession?.id === id) setSelectedSession(null);
      toast.success('Session deleted');
    } catch { toast.error('Delete failed'); }
  };

  const uploadKB = async () => {
    if (!kbAvatarId) return toast.error('Select an avatar first');
    if (!kbFile) return toast.error('Choose a file');
    const fd = new FormData();
    fd.append('file', kbFile);
    if (kbLabel) fd.append('label', kbLabel);
    setUploading(true);
    try {
      const r = await api.post(`/sankat-mochan/admin/avatar/${kbAvatarId}/knowledge`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded — ${r.data.chunks_added} chunks added`);
      setKbFile(null); setKbLabel('');
      loadKB(kbAvatarId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const deleteKBSource = async (sourceFile) => {
    if (!window.confirm(`Delete all chunks from "${sourceFile}"?`)) return;
    try {
      await api.delete(`/sankat-mochan/admin/avatar/${kbAvatarId}/knowledge?source_file=${encodeURIComponent(sourceFile)}`);
      loadKB(kbAvatarId);
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-6" data-testid="sankat-vip-panel">
      {/* ---- Duration config ---- */}
      <div className="rounded-2xl bg-slate-900/70 border border-amber-400/30 p-5">
        <div className="text-amber-300 text-sm font-bold uppercase tracking-widest mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4" /> VIP Session Duration
        </div>
        <div className="flex items-center gap-4">
          <input
            type="range"
            min="60" max="3600" step="60"
            value={durationInput}
            onChange={(e) => setDurationInput(parseInt(e.target.value, 10))}
            data-testid="vip-duration-slider"
            className="flex-1 accent-amber-400"
          />
          <div className="text-white font-mono text-lg font-bold min-w-[100px] text-right">
            {Math.floor(durationInput / 60)} min
          </div>
          <button onClick={saveDuration} data-testid="vip-duration-save"
            className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-bold flex items-center gap-2">
            <Save className="w-4 h-4" /> Save
          </button>
        </div>
        <div className="text-slate-400 text-xs mt-2">
          Current default: <b className="text-amber-300">{Math.floor(config.duration_sec / 60)} min</b> · Auto-delete after <b>{config.auto_delete_hours}h</b> · Range: 1-60 min
        </div>
      </div>

      {/* ---- Saved sessions (all users) ---- */}
      <div className="rounded-2xl bg-slate-900/70 border border-fuchsia-400/30 p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-fuchsia-300 text-sm font-bold uppercase tracking-widest flex items-center gap-2">
            <MessageSquare className="w-4 h-4" /> All Saved VIP Sessions ({sessions.length})
          </div>
          <button onClick={loadSessions} className="text-xs text-fuchsia-300 hover:text-fuchsia-200">↻ Refresh</button>
        </div>
        <div className="grid md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
          {sessions.map((s) => (
            <div key={s.id} className="rounded-lg bg-slate-800/60 border border-slate-700/60 p-3 text-xs">
              <div className="flex items-center justify-between mb-1">
                <div className="text-amber-200 font-bold truncate flex-1">{s.avatar_name || 'Session'}</div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${s.status === 'active' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-500/20 text-slate-300'}`}>{s.status}</span>
              </div>
              <div className="text-slate-400 flex items-center gap-2 mb-2">
                <UserIcon className="w-3 h-3" /> {s.user_id?.slice(0, 8)}… · {(s.messages || []).length} msgs
                {s.extensions ? <span className="text-emerald-300">· ext×{s.extensions}</span> : null}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setSelectedSession(s)} data-testid={`vip-view-${s.id}`}
                  className="flex-1 py-1.5 rounded bg-fuchsia-500/20 text-fuchsia-200 hover:bg-fuchsia-500 hover:text-white text-[11px] font-bold flex items-center justify-center gap-1">
                  <ChevronRight className="w-3 h-3" /> View chat
                </button>
                <button onClick={() => deleteSession(s.id)} data-testid={`vip-delete-${s.id}`}
                  className="w-8 h-8 rounded bg-rose-500/20 text-rose-300 hover:bg-rose-500 hover:text-white flex items-center justify-center">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
          {sessions.length === 0 && <div className="text-slate-500 text-sm col-span-2 text-center py-6">No saved sessions yet.</div>}
        </div>
      </div>

      {/* ---- Session viewer modal ---- */}
      {selectedSession && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4" data-testid="vip-session-viewer">
          <div className="max-w-2xl w-full max-h-[85vh] rounded-2xl bg-slate-900 border-2 border-fuchsia-400/40 flex flex-col">
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <div className="text-amber-200 font-bold">{selectedSession.avatar_name} · {selectedSession.user_id?.slice(0, 12)}…</div>
              <button onClick={() => setSelectedSession(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {(selectedSession.messages || []).map((m, i) => (
                <div key={i} className={`p-2.5 rounded-lg text-sm ${m.role === 'user' ? 'bg-slate-800 text-slate-200' : 'bg-amber-500/10 border border-amber-400/30 text-amber-100'}`}>
                  <div className="text-[10px] uppercase text-slate-400 font-bold mb-1">{m.role === 'user' ? 'User' : selectedSession.avatar_name}</div>
                  <div className="whitespace-pre-wrap">{m.text}</div>
                </div>
              ))}
              {(!selectedSession.messages || selectedSession.messages.length === 0) && <div className="text-slate-500 text-center py-8">No messages in this session.</div>}
            </div>
          </div>
        </div>
      )}

      {/* ---- Knowledge Base upload per avatar ---- */}
      <div className="rounded-2xl bg-slate-900/70 border border-emerald-400/30 p-5">
        <div className="text-emerald-300 text-sm font-bold uppercase tracking-widest mb-3 flex items-center gap-2">
          <Database className="w-4 h-4" /> Avatar Knowledge Base (RAG)
        </div>
        <div className="text-slate-400 text-xs mb-3">
          Upload PDF / TXT / MD reference material. Chunks are automatically injected into the avatar&apos;s system prompt on each user message so the AI reasons using your curated knowledge.
        </div>
        <div className="grid md:grid-cols-3 gap-3 mb-4">
          <select
            value={kbAvatarId}
            onChange={(e) => setKbAvatarId(e.target.value)}
            data-testid="kb-avatar-select"
            className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm"
          >
            <option value="">— Select Avatar —</option>
            {avatars.map((a) => <option key={a.id} value={a.id}>{a.display_name}</option>)}
          </select>
          <input
            type="file"
            accept=".txt,.md,.pdf,.csv,.json"
            onChange={(e) => setKbFile(e.target.files?.[0] || null)}
            data-testid="kb-file-input"
            className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm file:mr-2 file:px-3 file:py-1 file:rounded file:border-0 file:bg-emerald-500 file:text-black file:font-bold file:cursor-pointer"
          />
          <input
            type="text"
            placeholder="Optional label"
            value={kbLabel}
            onChange={(e) => setKbLabel(e.target.value)}
            data-testid="kb-label-input"
            className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm"
          />
        </div>
        <button onClick={uploadKB} disabled={uploading || !kbAvatarId || !kbFile}
          data-testid="kb-upload-btn"
          className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-bold flex items-center gap-2 disabled:opacity-50">
          <Upload className="w-4 h-4" /> {uploading ? 'Uploading…' : 'Upload Knowledge'}
        </button>

        {kbAvatarId && (
          <div className="mt-4">
            <div className="text-xs text-slate-400 uppercase font-bold mb-2">Uploaded sources ({kbSources.length})</div>
            <div className="space-y-2">
              {kbSources.map((s) => (
                <div key={s.source_file} className="rounded-lg bg-slate-800/60 border border-slate-700/60 p-3 text-xs">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-emerald-200 font-bold truncate">{s.label || s.source_file}</div>
                      <div className="text-slate-500">{s.chunks} chunks · {s.source_file}</div>
                      {s.sample && <div className="text-slate-400 italic mt-1 line-clamp-2">{s.sample}</div>}
                    </div>
                    <button onClick={() => deleteKBSource(s.source_file)} data-testid={`kb-delete-${s.source_file}`}
                      className="w-8 h-8 rounded bg-rose-500/20 text-rose-300 hover:bg-rose-500 hover:text-white flex items-center justify-center flex-shrink-0 ml-2">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
              {kbSources.length === 0 && <div className="text-slate-500 text-sm text-center py-3">No knowledge uploaded for this avatar yet.</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SankatVIPPanel;
